import { SectionDeclaration } from "../../../../../Generator/src/generators/DataDeclarationGenerator/DataDeclaration";
import { iterateKeys } from "../../../../../Common/IterableUtils";
import {
    AbsoluteModelPath,
    AbsoluteResolvedModelPath,
    convertToAbsoluteModelPath,
    convertToLegacyPath,
    createAbsoluteFromMask,
    createAbsoluteFromTokens,
    PathTokens,
} from "../../../../../Common/ModelPath/ModelPath";
import { isNotNullOrUndefined } from "../../../../../Common/TypingUtils";
import { IFetchFunctionCallContextProvider } from "../../Form/CustomizationContexts/FetchFunctionContext/IFetchFunctionCallContextProvider";
import { BindingPath } from "../CoreTypes";
import { MaskRegistrator } from "../PathUtils/MaskRegistrator";
import { SectionName } from "../../../Controls/FormParts/Unit/UnitId/UnitId";
import { AbsoluteModelPathSet } from "../../../../../Common/ModelPath/Set/AbsoluteModelPathSet";
import { RngSchemeTypes } from "../../../../../Common/RngSchemeTypes/types";
import { AttachFormat } from "../../../../../Common/CandyApiClient/Types/Attachments/Attacments";

import { DataDeclaration, NodeDataDeclaration, NodeFieldValueSource } from "./DataDeclaration";

export class DataDeclarationHelper {
    private readonly fetchFunctionCallContextProvider: IFetchFunctionCallContextProvider;
    private dataDeclaration: DataDeclaration;
    private optionalSections: SectionDeclaration[];
    private allSingleSections: SectionDeclaration[];
    private dataDeclarationMasks: MaskRegistrator;
    private allPaths: readonly AbsoluteModelPath[];

    public constructor(
        fetchFunctionCallContextProvider: IFetchFunctionCallContextProvider,
        dataDeclaration: DataDeclaration,
        formRoot: string
    ) {
        this.fetchFunctionCallContextProvider = fetchFunctionCallContextProvider;
        this.dataDeclaration = dataDeclaration;
        this.dataDeclarationMasks = new MaskRegistrator();
        this.optionalSections = this.dataDeclaration[formRoot]?.optionalSections ?? [];
        this.allSingleSections = this.dataDeclaration[formRoot]?.allSingleSections ?? [];

        const allPaths: AbsoluteModelPath[] = [];
        for (const declMask of iterateKeys(this.dataDeclaration)) {
            this.dataDeclarationMasks.registerMask(declMask, undefined);
            allPaths.push(createAbsoluteFromMask(declMask, PathTokens.each));
        }
        this.allPaths = allPaths;
    }

    public reloadDataDeclaration(formRoot: string, dataDeclaration: DataDeclaration) {
        this.dataDeclaration = dataDeclaration;
        this.dataDeclarationMasks = new MaskRegistrator();
        this.allSingleSections = this.dataDeclaration[formRoot]?.allSingleSections ?? [];
        this.optionalSections = this.dataDeclaration[formRoot]?.optionalSections ?? [];

        const allPaths: AbsoluteModelPath[] = [];
        for (const declMask of iterateKeys(this.dataDeclaration)) {
            this.dataDeclarationMasks.registerMask(declMask, undefined);
            allPaths.push(createAbsoluteFromMask(declMask, PathTokens.each));
        }
        this.allPaths = allPaths;
    }

    public getAllPaths(): readonly AbsoluteModelPath[] {
        return this.allPaths;
    }

    public hasMask(nodePath: BindingPath): boolean {
        return this.getMask(nodePath) != undefined;
    }

    public getMask(nodePath: AbsoluteModelPath | BindingPath): undefined | BindingPath {
        return this.dataDeclarationMasks.getMasks(nodePath)[0];
    }

    public isNodeHasChildrenEntry(nodePath: BindingPath | AbsoluteModelPath): boolean {
        return this.getEntry(nodePath).children != undefined;
    }

    public isNodeHasAutoFlagEntry(nodePath: BindingPath | AbsoluteResolvedModelPath): boolean {
        return this.getEntry(nodePath).autoFlag != undefined;
    }

    public getType(nodePath: BindingPath | AbsoluteResolvedModelPath): RngSchemeTypes | undefined {
        return this.getEntry(nodePath)?.type;
    }

    public isErrorsAndWarningsCountRequired(nodePath: BindingPath | AbsoluteModelPath): boolean {
        return this.getEntry(nodePath).errorAndWarningCountRequired?.[0] ?? false;
    }

    public isDisableValueAutoInit(nodePath: BindingPath | AbsoluteModelPath): boolean | undefined {
        return this.getEntry(nodePath).hasDisableValueAutoInitFlag;
    }

    public getEntry(nodePath: AbsoluteModelPath | BindingPath): NodeDataDeclaration {
        const mask = this.getMask(nodePath);
        return (mask != undefined ? this.dataDeclaration[mask] : undefined) ?? {};
    }

    public getSections(nodePath: BindingPath | AbsoluteModelPath): SectionName[] {
        return (
            this.getSectionsStrict(nodePath) ?? this.getSections(convertToAbsoluteModelPath(nodePath).getParentPath())
        );
    }

    public getSectionsStrict(nodePath: BindingPath | AbsoluteModelPath): undefined | SectionName[] {
        const node = this.getEntry(nodePath);

        let sections;
        const section: undefined | NodeFieldValueSource | NodeFieldValueSource[] = (node.section || [])[0];

        if (typeof section === "string") {
            sections = [section];
        } else if (Array.isArray(section)) {
            sections = section.slice(0);

            sections = sections
                .map(x => {
                    if (x != undefined && typeof x === "object" && "fn" in x && typeof x.fn === "function") {
                        const fnResult = x.fn.call(
                            this.fetchFunctionCallContextProvider.getFetchFunctionCallContext(undefined),
                            convertToLegacyPath(nodePath)
                        );
                        if (Array.isArray(fnResult)) {
                            return fnResult.slice(0);
                        }
                        return fnResult;
                    }
                    return x;
                })
                .flat();
        } else if (
            section != undefined &&
            typeof section === "object" &&
            "fn" in section &&
            typeof section.fn === "function"
        ) {
            const fnResult = section.fn.call(
                this.fetchFunctionCallContextProvider.getFetchFunctionCallContext(undefined),
                convertToLegacyPath(nodePath)
            );
            sections = Array.isArray(fnResult) ? fnResult : [fnResult];
        } else {
            return undefined;
        }

        // @ts-ignore
        return [...new Set(sections.filter(isNotNullOrUndefined))];
    }

    public getTargetFormat(path: BindingPath | AbsoluteResolvedModelPath, fileName: string): AttachFormat | undefined {
        const extension = fileName?.split(".").pop()?.toLowerCase();
        if (!extension) {
            return undefined;
        }

        if (this.isFnsConvertFile(path, fileName)) {
            return AttachFormat.PdfA1_1_4;
        }

        if (extension === "pdf") {
            return AttachFormat.Pdf;
        }

        return undefined;
    }

    public isFnsConvertFile(path: BindingPath | AbsoluteResolvedModelPath, fileName?: string): boolean {
        const extension = fileName?.split(".").slice(-1)[0];
        const mask = this.getMask(path);
        const { fnsConvertFileExtentions } = (mask != undefined ? this.dataDeclaration[mask] : undefined) ?? {};
        if (extension == undefined) {
            return false;
        }
        return fnsConvertFileExtentions?.includes(extension) ?? false;
    }

    public getOptionalSections(): readonly SectionDeclaration[] {
        return this.optionalSections;
    }

    public getAllSingleSections(): readonly SectionDeclaration[] {
        return this.allSingleSections;
    }

    public buildSectionToMasksMap(): Map<SectionName, AbsoluteModelPathSet> {
        const section2masks = new Map<SectionName, AbsoluteModelPathSet>();
        for (const path of this.getAllPaths()) {
            const sections = this.getSections(path);

            for (const sectionName of sections) {
                if (!section2masks.has(sectionName)) {
                    section2masks.set(sectionName, new AbsoluteModelPathSet());
                }
                section2masks.get(sectionName)?.add(path);
            }
        }
        return section2masks;
    }

    public *getMasksByPrefix(nodePathPrefix: AbsoluteModelPath): Iterable<AbsoluteModelPath> {
        const prefixTokens = nodePathPrefix.getPathPartsAsArray();

        for (const dataDeclarationPath of this.getAllPaths()) {
            const dataDeclarationPathTokens = [...dataDeclarationPath.getPathParts()];

            if (dataDeclarationPathTokens.length < prefixTokens.length) {
                continue;
            }
            let matches = true;

            for (let i = 0; i < prefixTokens.length; ++i) {
                const prefToken = prefixTokens[i];
                const declToken = dataDeclarationPathTokens[i];

                if (prefToken === declToken) {
                    continue;
                }
                if (
                    prefToken != undefined &&
                    PathTokens.isInstanceToken(prefToken) &&
                    PathTokens.isMultiToken(declToken)
                ) {
                    dataDeclarationPathTokens[i] = prefToken;
                    continue;
                }
                matches = false;
                break;
            }
            if (matches) {
                yield createAbsoluteFromTokens(dataDeclarationPathTokens);
            }
        }
    }
}
